/**
 * Utility script to add Python Advanced and Data Analytics courses
 * with all interactive components assigned at course level
 */

import { coursesService } from '../services/firestoreService';
import { LearningComponentsService } from '../services/learningComponentsService';
import { pythonAdvancedCourseData, pythonAdvancedComponents } from './pythonAdvancedCourse';
import { dataAnalyticsCourseData, dataAnalyticsComponents } from './dataAnalyticsCourse';

/**
 * Get component IDs by name from the learning components library
 */
async function getComponentIdsByNames(componentNames) {
  try {
    const allComponents = await LearningComponentsService.getAllComponents();
    const componentMap = new Map();
    
    allComponents.forEach(comp => {
      componentMap.set(comp.name, comp.id);
    });
    
    const componentIds = [];
    for (const name of componentNames) {
      const id = componentMap.get(name);
      if (id) {
        componentIds.push(id);
      } else {
        console.warn(`⚠️ Component "${name}" not found in library`);
      }
    }
    
    return componentIds;
  } catch (error) {
    console.error('Error getting component IDs:', error);
    return [];
  }
}

/**
 * Add Python Advanced course with interactive components
 */
export async function addPythonAdvancedCourse(creatorId, creatorName, creatorEmail) {
  try {
    console.log('🚀 Adding Python Advanced Course...');
    
    // Create the course
    const courseData = {
      ...pythonAdvancedCourseData,
      createdBy: creatorId,
      createdByName: creatorName,
      createdByEmail: creatorEmail,
      isActive: true,
      enrolledStudents: 0,
      rating: 0,
      reviews: 0
    };
    
    const courseId = await coursesService.createCourse(courseData);
    console.log(`✅ Course created: ${courseId}`);
    
    // Get component IDs
    const componentIds = await getComponentIdsByNames(pythonAdvancedComponents);
    
    if (componentIds.length > 0) {
      // Add components to course
      await LearningComponentsService.addComponentsToCourse(
        courseId,
        componentIds,
        creatorId
      );
      console.log(`✅ Added ${componentIds.length} interactive components to course`);
    } else {
      console.warn('⚠️ No components found to add');
    }
    
    return { courseId, componentCount: componentIds.length };
  } catch (error) {
    console.error('❌ Error adding Python Advanced course:', error);
    throw error;
  }
}

/**
 * Add Data Analytics course with interactive components
 */
export async function addDataAnalyticsCourse(creatorId, creatorName, creatorEmail) {
  try {
    console.log('🚀 Adding Data Analytics Course...');
    
    // Create the course
    const courseData = {
      ...dataAnalyticsCourseData,
      createdBy: creatorId,
      createdByName: creatorName,
      createdByEmail: creatorEmail,
      isActive: true,
      enrolledStudents: 0,
      rating: 0,
      reviews: 0
    };
    
    const courseId = await coursesService.createCourse(courseData);
    console.log(`✅ Course created: ${courseId}`);
    
    // Get component IDs
    const componentIds = await getComponentIdsByNames(dataAnalyticsComponents);
    
    if (componentIds.length > 0) {
      // Add components to course
      await LearningComponentsService.addComponentsToCourse(
        courseId,
        componentIds,
        creatorId
      );
      console.log(`✅ Added ${componentIds.length} interactive components to course`);
    } else {
      console.warn('⚠️ No components found to add');
    }
    
    return { courseId, componentCount: componentIds.length };
  } catch (error) {
    console.error('❌ Error adding Data Analytics course:', error);
    throw error;
  }
}

/**
 * Add both courses
 */
export async function addBothAdvancedCourses(creatorId, creatorName, creatorEmail) {
  try {
    console.log('🎯 Adding both advanced courses...\n');
    
    const pythonResult = await addPythonAdvancedCourse(creatorId, creatorName, creatorEmail);
    console.log('\n');
    const analyticsResult = await addDataAnalyticsCourse(creatorId, creatorName, creatorEmail);
    
    console.log('\n🎉 All courses added successfully!');
    console.log(`\n📊 Summary:`);
    console.log(`   Python Advanced: ${pythonResult.componentCount} components`);
    console.log(`   Data Analytics: ${analyticsResult.componentCount} components`);
    
    return {
      python: pythonResult,
      analytics: analyticsResult
    };
  } catch (error) {
    console.error('❌ Error adding courses:', error);
    throw error;
  }
}

